import {
  PersistedChannel,
  PreviewContact,
  ResolvedContactCard,
} from "@/components/chat/contact-reveal-card";
import ErrorMessage from "@/components/error-message";
import { MobileSheet } from "@/components/ui/mobile-sheet";
import { SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { useTeams } from "@/contexts/teams-provider";
import { useApiQuery } from "@/hooks/useApiQuery";
import { useTranslation } from "@/hooks/useTranslation";
import { searchKeys } from "@/lib/query-keys";
import { SavedSearchDTO, SearchEngineEmployeeResult } from "@shaarp/common";
import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Cards mounted at a time, and added each time the end of the list comes into
 * sight: every card reads its contact's live reveal state on mount, so a table
 * of 100 must not fire 100 reads the moment the sheet opens.
 */
export const PEOPLE_CARDS_PAGE_SIZE = 10;

/** A row of the saved table as the contact card reads it. */
const toPreviewContact = (
  employee: SearchEngineEmployeeResult,
): PreviewContact => ({
  firstName: employee.firstName,
  lastName: employee.lastName,
  jobTitle: employee.jobTitle,
  company: employee.companyName || employee.domain || null,
  city: employee.city,
  country: employee.country,
  linkedInUrl: employee.linkedInUrl,
  photoUrl: employee.photoUrl,
});

/**
 * The channels the team's plan keeps open: a card would only be refused a
 * channel the team has cut. A flag the stored team does not carry yet reads
 * as open, like the contact card's — the API refuses a cut channel either way.
 */
const useOpenChannels = (): PersistedChannel[] => {
  const { teamSelected } = useTeams();
  const isEmailOpen = teamSelected?.featureContactRevealEmailEnabled ?? true;
  const isPhoneOpen = teamSelected?.featureContactRevealPhoneEnabled ?? true;
  return useMemo(
    () => [
      ...(isEmailOpen ? [{ channel: "email" as const }] : []),
      ...(isPhoneOpen ? [{ channel: "phone" as const }] : []),
    ],
    [isEmailOpen, isPhoneOpen],
  );
};

/**
 * A people table of the conversation, on a phone: the table has no room
 * there, so its people are stacked as the chat's contact cards — reveal,
 * write, call, send to the CRM — in a sheet over the conversation.
 */
export function ChatPeopleCardsSheet({
  open,
  onOpenChange,
  searchId,
  query,
  peopleCount,
  onSendMessage,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly searchId: string;
  readonly query: string | undefined;
  readonly peopleCount: number;
  /** Sends a follow-up in the conversation (a hook email, a call script). */
  readonly onSendMessage?: (text: string) => void;
}) {
  const { t } = useTranslation();

  // The follow-up lands in the thread under the sheet: close it, or the
  // answer streams where the user cannot see it.
  const sendAndClose = onSendMessage
    ? (text: string): void => {
        onOpenChange(false);
        onSendMessage(text);
      }
    : undefined;

  return (
    <MobileSheet open={open} onOpenChange={onOpenChange}>
      <div className="px-4 pb-3">
        <SheetTitle className="text-base font-semibold truncate">
          {query || t("chat.peopleTable.untitled")}
        </SheetTitle>
        <SheetDescription className="text-xs text-muted-foreground">
          {t("chat.peopleTable.count", { count: peopleCount })}
        </SheetDescription>
      </div>
      {/* Mounted only while the sheet is open (the sheet unmounts its
          content), so each opening starts again from the first cards. */}
      <PeopleCardsList searchId={searchId} onSendMessage={sendAndClose} />
    </MobileSheet>
  );
}

/** The saved table's people, a page of cards at a time. */
function PeopleCardsList({
  searchId,
  onSendMessage,
}: {
  readonly searchId: string;
  readonly onSendMessage?: (text: string) => void;
}) {
  const { teamSelected } = useTeams();
  const teamId = teamSelected?.id ?? "";
  const channels = useOpenChannels();
  // Same entry as the desktop panel's: a table read there is not read again.
  const { data: saved, isError } = useApiQuery<SavedSearchDTO>({
    key: searchKeys.detail(teamId, searchId),
    path: `/search-engine/search/${searchId}`,
    enabled: !!teamId && !!searchId,
    refetchOnWindowFocus: false,
  });
  const employees = saved?.employees ?? [];

  const [visibleCount, setVisibleCount] = useState(PEOPLE_CARDS_PAGE_SIZE);
  const hasMore = visibleCount < employees.length;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || typeof IntersectionObserver === "undefined") {
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisibleCount((count) => count + PEOPLE_CARDS_PAGE_SIZE);
        }
      },
      { root: scrollRef.current, rootMargin: "200px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
    // Re-observed after each page: a sentinel still in sight after the
    // cards it unlocked were added fires again.
  }, [hasMore, visibleCount]);

  return (
    <div
      ref={scrollRef}
      className="overflow-y-auto overscroll-y-contain flex-1 px-4 pb-4 min-h-0"
    >
      {isError ? (
        <ErrorMessage className="pt-10" />
      ) : !saved ? (
        <PeopleCardsSkeleton />
      ) : (
        <div className="flex flex-col gap-3">
          {employees.slice(0, visibleCount).map((employee) => (
            <ResolvedContactCard
              key={employee.id}
              employeeId={employee.id}
              contact={toPreviewContact(employee)}
              channels={channels}
              onSendMessage={onSendMessage}
            />
          ))}
          {hasMore && (
            <div ref={sentinelRef} data-testid="people-cards-sentinel">
              <ContactCardSkeleton />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Stands in for the cards while the saved table loads. */
function PeopleCardsSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      {Array.from({ length: 3 }, (_, index) => (
        <ContactCardSkeleton key={index} />
      ))}
    </div>
  );
}

/** One contact card's shape: identity, then its channel rows. */
function ContactCardSkeleton() {
  return (
    <div className="p-4 space-y-3 rounded-xl border">
      <div className="flex gap-3 items-center">
        <Skeleton className="rounded-full size-11 shrink-0" />
        <div className="flex-1 space-y-2">
          <Skeleton className="w-32 h-3.5" />
          <Skeleton className="w-48 h-3" />
        </div>
      </div>
      <Skeleton className="w-full h-9" />
      <Skeleton className="w-full h-9" />
    </div>
  );
}
